/**
 * Omnichannel API Service
 */
import {
  ClientContactInfo,
  ClientInfoResponse,
  ConversationListResponse,
  DraftSendRequest,
  DraftUpdateRequest,
  MessageListResponse,
  OmnichannelMessage,
  SendMessageRequest,
  UpdateClientNameRequest,
} from '../types/omnichannel';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const API_PREFIX = '/api/v1/omnichannel';

class OmnichannelApiService {
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('access_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
        throw new Error('Authentication failed - redirecting to login');
      }

      const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  }

  /**
   * Get list of conversations (clients with message stats)
   */
  async getConversations(params?: {
    project_id?: number;
    query?: string;
    source?: string;
    has_drafts?: boolean;
    page?: number;
    size?: number;
  }): Promise<ConversationListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.project_id) searchParams.append('project_id', String(params.project_id));
    if (params?.query) searchParams.append('query', params.query);
    if (params?.source) searchParams.append('source', params.source);
    if (params?.has_drafts !== undefined) searchParams.append('has_drafts', String(params.has_drafts));
    if (params?.page) searchParams.append('page', String(params.page));
    if (params?.size) searchParams.append('size', String(params.size));

    const queryString = searchParams.toString();
    const url = `${API_BASE_URL}${API_PREFIX}/conversations${queryString ? `?${queryString}` : ''}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse<ConversationListResponse>(response);
  }

  /**
   * Get messages for a specific client conversation
   */
  async getConversationMessages(
    clientId: number,
    params?: { page?: number; size?: number; peer_address?: string }
  ): Promise<MessageListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', String(params.page));
    if (params?.size) searchParams.append('size', String(params.size));
    if (params?.peer_address) searchParams.append('peer_address', params.peer_address);

    const queryString = searchParams.toString();
    const url = `${API_BASE_URL}${API_PREFIX}/conversations/${clientId}/messages${queryString ? `?${queryString}` : ''}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse<MessageListResponse>(response);
  }

  /**
   * Get available communication channels for a client
   */
  async getClientContacts(clientId: number): Promise<ClientContactInfo[]> {
    const response = await fetch(`${API_BASE_URL}${API_PREFIX}/conversations/${clientId}/contacts`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse<ClientContactInfo[]>(response);
  }

  /**
   * Send a new message to a client
   */
  async sendMessage(request: SendMessageRequest): Promise<OmnichannelMessage> {
    const response = await fetch(`${API_BASE_URL}${API_PREFIX}/messages`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request),
    });
    return this.handleResponse<OmnichannelMessage>(response);
  }

  /**
   * Get a draft message by ID
   */
  async getDraft(messageId: number): Promise<OmnichannelMessage> {
    const response = await fetch(`${API_BASE_URL}${API_PREFIX}/drafts/${messageId}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse<OmnichannelMessage>(response);
  }

  /**
   * Update a draft message content
   */
  async updateDraft(messageId: number, request: DraftUpdateRequest): Promise<OmnichannelMessage> {
    const response = await fetch(`${API_BASE_URL}${API_PREFIX}/drafts/${messageId}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request),
    });
    return this.handleResponse<OmnichannelMessage>(response);
  }

  /**
   * Delete a draft message
   */
  async deleteDraft(messageId: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}${API_PREFIX}/drafts/${messageId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    await this.handleResponse<void>(response);
  }

  /**
   * Send a draft message
   */
  async sendDraft(messageId: number, request?: DraftSendRequest): Promise<OmnichannelMessage> {
    const response = await fetch(`${API_BASE_URL}${API_PREFIX}/drafts/${messageId}/send`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request || {}),
    });
    return this.handleResponse<OmnichannelMessage>(response);
  }

  /**
   * Update client name or create new client from orphan messages
   */
  async updateClientName(clientId: number, request: UpdateClientNameRequest): Promise<ClientInfoResponse> {
    const response = await fetch(`${API_BASE_URL}${API_PREFIX}/conversations/${clientId}/name`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request),
    });
    return this.handleResponse<ClientInfoResponse>(response);
  }
}

// Export singleton instance
export const omnichannelApi = new OmnichannelApiService();


